import analytics from "../../dependencies/analytics";
import { RESTAURANT_PERFORMANCE_MONITORING_ENABLED } from "../../dependencies/env";
import { isServer } from "../../dependencies/utils";
import type { Time } from "../../model/times/model";
import type { DiscountAvailabilityById } from "../../model/types";
import { walkInDiscountAvailabilityStrategy } from "../../model/walkIn/utils/walkInDiscountAvailabilityStrategy";
import {
  type DiscountAvailabilityStrategy,
  firstTableDiscountAvailabilityStrategy,
} from "../discountAvailability/discountAvailabilityStrategy";
import hasDiscountAvailability from "../discountAvailability/hasDiscountAvailability";

/**
 * Fields the clock reads when an attempt settles. Structural so `page.nextPage`
 * can pass root state without importing the store model (that import cycles).
 */
export type ListInitialLoadSnapshot = {
  /** Per-store key. Nested stores (a map card) must not share an attempt. */
  deriveCacheIdentity: object;
  idsToShow: readonly number[];
  pagedIds: readonly number[];
  byId: { value: { [restaurantId: number]: unknown } };
  discountAvailabilityById: DiscountAvailabilityById;
  effectiveDate: string;
  times: { isSet: boolean; start: Time; end: Time };
  people: { value?: number };
  hasLastTable: { value: boolean };
  walkIn: { enabled: boolean };
};

const JOURNEY = "list_initial_load";

type Phase = "idle" | "open" | "ready";
type Outcome = "with_availability" | "no_availability";

function canMeasure() {
  return (
    RESTAURANT_PERFORMANCE_MONITORING_ENABLED &&
    !isServer() &&
    typeof performance !== "undefined"
  );
}

type Attempt = {
  phase: Phase;
  startTime: number | undefined;
  outcome: Outcome | undefined;
  readyListeners: Set<() => void>;
};

const attempts = new WeakMap<object, Attempt>();

function getAttempt(store: object): Attempt {
  const existing = attempts.get(store);
  if (existing) {
    return existing;
  }

  const created: Attempt = {
    phase: "idle",
    startTime: undefined,
    outcome: undefined,
    readyListeners: new Set(),
  };
  attempts.set(store, created);
  return created;
}

function currentPathname() {
  // React Native defines `window` without `location`
  if (typeof window === "undefined" || window.location == null) {
    return undefined;
  }

  return window.location.pathname;
}

function dropAttempt(attempt: Attempt) {
  attempt.phase = "idle";
  attempt.startTime = undefined;
  attempt.outcome = undefined;
}

function send(attempt: Attempt, success: boolean, listOutcome?: Outcome) {
  if (attempt.startTime === undefined) {
    dropAttempt(attempt);
    return;
  }

  const durationMs = Math.round(performance.now() - attempt.startTime);
  const pathname = currentPathname();
  dropAttempt(attempt);

  if (!Number.isFinite(durationMs) || durationMs < 0) {
    return;
  }

  analytics.track("journey_timing", {
    journey: JOURNEY,
    durationMs,
    success,
    ...(listOutcome !== undefined ? { outcome: listOutcome } : {}),
    ...(pathname !== undefined ? { pathname } : {}),
  });
}

/**
 * Bookable discount slot on the settled page, or none. Stops at the first hit.
 * Session is already applied when availability is derived, except Last Table,
 * which narrows to Dinner2 the same way the discount section does. Walk-in
 * uses its own slot rules so a walk-in list is not scored as First Table.
 */
export function classifyListInitialLoadOutcome(
  state: ListInitialLoadSnapshot,
): Outcome {
  const walkInEnabled = state.walkIn.enabled;
  const strategy: DiscountAvailabilityStrategy = walkInEnabled
    ? walkInDiscountAvailabilityStrategy
    : firstTableDiscountAvailabilityStrategy;
  const session =
    !walkInEnabled && state.hasLastTable.value ? "Dinner2" : undefined;

  for (const restaurantId of state.idsToShow) {
    const hasAvailability = hasDiscountAvailability(
      state.discountAvailabilityById,
      state.effectiveDate,
      restaurantId,
      state.times.isSet,
      state.times.start,
      state.times.end,
      state.people.value,
      session,
      strategy,
    );

    if (hasAvailability) {
      return "with_availability";
    }
  }

  return "no_availability";
}

function everyPagedDetailFailed(state: ListInitialLoadSnapshot) {
  const { pagedIds } = state;

  return (
    pagedIds.length > 0 && pagedIds.every((id) => state.byId.value[id] === null)
  );
}

/** Drop an open or unsent attempt. A sample already sent is left alone. */
export function cancelListInitialLoad(store: object) {
  const attempt = getAttempt(store);
  if (!canMeasure() || attempt.phase === "idle") {
    return;
  }

  dropAttempt(attempt);
}

/** Open a new attempt, dropping any previous one that has not been sent. */
export function beginListInitialLoad(store: object) {
  if (!canMeasure()) {
    return;
  }

  const attempt = getAttempt(store);
  dropAttempt(attempt);
  attempt.phase = "open";
  attempt.startTime = performance.now();
}

/**
 * Initial load has settled (expansion finished, or nextPage froze the page
 * that just settled). Records the outcome and waits for the host to confirm
 * paint. Duration is measured at that confirmation so an empty list includes
 * the host's debounce before the empty message commits.
 */
export function settleListInitialLoad(state: ListInitialLoadSnapshot) {
  const attempt = getAttempt(state.deriveCacheIdentity);
  if (!canMeasure() || attempt.phase !== "open") {
    return;
  }

  if (everyPagedDetailFailed(state)) {
    send(attempt, false);
    return;
  }

  attempt.outcome = classifyListInitialLoadOutcome(state);
  attempt.phase = "ready";

  for (const listener of [...attempt.readyListeners]) {
    listener();
  }
}

/** nextPage: close the open attempt on the page that just settled. */
export function freezeOpenListInitialLoad(state: ListInitialLoadSnapshot) {
  settleListInitialLoad(state);
}

/** Host confirmed a card or the empty message committed. No-op until the attempt is ready. */
export function completeListInitialLoad(store: object) {
  const attempt = getAttempt(store);
  if (
    !canMeasure() ||
    attempt.phase !== "ready" ||
    attempt.outcome === undefined
  ) {
    return;
  }

  send(attempt, true, attempt.outcome);
}

/** Id fetch failed and was not aborted. */
export function failListInitialLoad(store: object) {
  const attempt = getAttempt(store);
  if (
    !canMeasure() ||
    (attempt.phase !== "open" && attempt.phase !== "ready")
  ) {
    return;
  }

  send(attempt, false);
}

/**
 * Fires when an attempt becomes ready. The list may already be on screen, in
 * which case the listener sends immediately, before a following page starts.
 */
export function subscribeListInitialLoadReady(
  store: object,
  listener: () => void,
) {
  const listeners = getAttempt(store).readyListeners;
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}
